import { useCallback, useRef, useState } from 'react';

const SWIPE_THRESHOLD_PX = 50;

const CLOSED = { isOpen: false, index: 0, direction: 0 };

const wrapIndex = (value, count) => ((value % count) + count) % count;

/**
 * Lightbox state: open/close, the current index, the direction of the last
 * step (1 next, -1 previous, 0 freshly opened) and horizontal swipe handlers.
 *
 * @param {Array} images - The collection the lightbox navigates.
 * @returns {{
 *   isOpen: boolean,
 *   index: number,
 *   direction: -1 | 0 | 1,
 *   open: (i: number) => void,
 *   close: () => void,
 *   next: () => void,
 *   prev: () => void,
 *   touchHandlers: { onTouchStart: Function, onTouchEnd: Function }
 * }}
 */
export default function useLightbox(images) {
    const [state, setState] = useState(CLOSED);
    const touchStart = useRef({ x: 0, y: 0 });

    const count = Array.isArray(images) ? images.length : 0;

    const open = useCallback((i) => {
        if (count === 0) return;
        setState({ isOpen: true, index: wrapIndex(i, count), direction: 0 });
    }, [count]);

    const close = useCallback(() => {
        setState((current) => (current.isOpen ? { ...current, isOpen: false } : current));
    }, []);

    const next = useCallback(() => {
        if (count === 0) return;
        setState((current) => ({ ...current, index: wrapIndex(current.index + 1, count), direction: 1 }));
    }, [count]);

    const prev = useCallback(() => {
        if (count === 0) return;
        setState((current) => ({ ...current, index: wrapIndex(current.index - 1, count), direction: -1 }));
    }, [count]);

    const onTouchStart = useCallback((event) => {
        const touch = event.changedTouches[0];
        touchStart.current = { x: touch.clientX, y: touch.clientY };
    }, []);

    // A swipe counts only when it is long enough and mostly horizontal,
    // so vertical scrolls and taps never change the image.
    const onTouchEnd = useCallback((event) => {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStart.current.x;
        const deltaY = touch.clientY - touchStart.current.y;
        if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) < Math.abs(deltaY)) return;
        if (deltaX < 0) next();
        else prev();
    }, [next, prev]);

    const safeIndex = count > 0 ? Math.min(state.index, count - 1) : 0;

    return {
        isOpen: state.isOpen,
        index: safeIndex,
        direction: state.direction,
        open,
        close,
        next,
        prev,
        touchHandlers: { onTouchStart, onTouchEnd }
    };
}
